import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { CalendarDays, Plus, Trash2, Loader2, Sparkles, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HolidaySettingsCard() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState("");
  const [adding, setAdding] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/settings/holidays`, { withCredentials: true });
      setHolidays(res.data.holidays || []);
    } catch (err) {
      toast.error("Failed to load holidays");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHolidays(); }, []);

  const handleAdd = async () => {
    if (!newName.trim() || !newDate) {
      toast.error("Name and date are required");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/api/admin/settings/holidays`, { name: newName.trim(), date: newDate }, { withCredentials: true });
      toast.success("Holiday added");
      setAddDialogOpen(false);
      setNewName("");
      setNewDate("");
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add holiday");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/api/admin/settings/holidays/${id}`, { withCredentials: true });
      toast.success("Holiday deleted");
      fetchHolidays();
    } catch (err) {
      toast.error("Failed to delete holiday");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/api/admin/settings/holidays/seed`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchHolidays();
    } catch (err) {
      toast.error("Failed to seed holidays");
    } finally {
      setSeeding(false);
    }
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  const isPast = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = dateStr.split("-");
    const d = new Date(year, month - 1, day);
    return d < today;
  };

  return (
    <>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
                  <CalendarDays className="h-5 w-5 text-slate-600" />
                  <CardTitle>Holiday Management</CardTitle>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              {expanded && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding}>
                    {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                    Seed US Holidays
                  </Button>
                  <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Holiday
                  </Button>
                </div>
              )}
            </div>
            {!expanded && (
              <CardDescription>
                {holidays.length > 0 ? `${holidays.length} holidays configured` : "Click to manage holidays"}
              </CardDescription>
            )}
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              <CardDescription className="mb-4">
                Manage holidays to prevent email reminders and daily reports from being sent on these days.
              </CardDescription>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : holidays.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CalendarDays className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p>No holidays configured.</p>
                  <p className="text-sm mt-1">Click "Seed US Holidays" to add national banking holidays.</p>
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left py-2 px-4 font-medium text-slate-600">Holiday</th>
                        <th className="text-left py-2 px-4 font-medium text-slate-600">Date</th>
                        <th className="text-right py-2 px-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidays.map((h) => (
                        <tr key={h.id} className={`border-b last:border-b-0 ${isPast(h.date) ? "opacity-50" : ""}`}>
                          <td className="py-2 px-4">{h.name}</td>
                          <td className="py-2 px-4 text-slate-600">{formatDate(h.date)}</td>
                          <td className="py-2 px-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(h.id)}
                              disabled={deletingId === h.id}
                            >
                              {deletingId === h.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Holiday Name</Label>
              <Input
                placeholder="e.g. Christmas Day"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={adding}>
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Add Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
